import React, { KeyboardEventHandler, useState } from 'react'
import { Flex, Text } from '@workwhile/ui'
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form'
import CreatableSelect from 'react-select/creatable'

type SpecificWorkerIdsFieldProps<T extends FieldValues> = {
  control: Control<T>
  name: FieldPath<T>
  errorMessage?: string
  label?: string
  helperText?: string
}

export const SpecificWorkerIdsField = <T extends FieldValues>({
  control,
  name,
  errorMessage,
  label = 'Request specific workers (optional)',
  helperText = 'Type a worker ID and press Enter. Workers must be active and not blocked by the company.',
}: SpecificWorkerIdsFieldProps<T>) => {
  const [inputValue, setInputValue] = useState('')

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const workerIds = (field.value as number[] | undefined) ?? []
        const values = workerIds.map((workerId) => ({
          label: workerId.toString(),
          value: workerId,
        }))

        const handleKeyDown: KeyboardEventHandler = (event) => {
          if (!inputValue) {
            return
          }
          if (event.key !== 'Enter' && event.key !== 'Tab') {
            return
          }

          const newWorkerId = Number(inputValue)
          if (!Number.isInteger(newWorkerId) || newWorkerId <= 0) {
            return
          }

          field.onChange(Array.from(new Set([...workerIds, newWorkerId])))
          setInputValue('')
          event.preventDefault()
        }

        return (
          <Flex flexDirection="column" gap={1}>
            <Text fontSize={1} fontWeight="bold">
              {label}
            </Text>
            <Text fontSize={0} color="gray" mb={1}>
              {helperText}
            </Text>
            <CreatableSelect
              isClearable
              isMulti
              menuPortalTarget={document.body}
              components={{
                DropdownIndicator: null,
              }}
              inputValue={inputValue}
              placeholder="Type worker ID and press Enter"
              menuIsOpen={false}
              onChange={(newValues) =>
                field.onChange(newValues?.map((option) => option.value) ?? [])
              }
              onInputChange={(newValue) => setInputValue(newValue)}
              onKeyDown={handleKeyDown}
              value={values}
            />
            {errorMessage ? (
              <Text fontSize={0} color="red">
                {errorMessage}
              </Text>
            ) : null}
          </Flex>
        )
      }}
    />
  )
}
